import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { API_CONFIG, API_ENDPOINTS } from '../config/api';

interface PortalClient {
  client_id: number;
  email: string;
  full_name: string;
  site_id: number;
}

interface ClientPortalState {
  client: PortalClient | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string, siteId: number) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

export const useClientPortalStore = create<ClientPortalState>()(
  persist(
    (set, _get) => ({
      client: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email, password, siteId) => {
        set({ isLoading: true, error: null });
        try {
          const res = await axios.post<{
            access_token: string;
            client_id: number;
            email: string;
            full_name: string;
            site_id: number;
          }>(`${API_CONFIG.FULL_URL}${API_ENDPOINTS.PORTAL.LOGIN}`, { email, password, site_id: siteId });

          const { access_token, ...clientData } = res.data;
          localStorage.setItem('client-portal-token', access_token);
          set({
            client: clientData,
            accessToken: access_token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (err: unknown) {
          const message =
            axios.isAxiosError(err) && err.response?.data?.error?.message
              ? err.response.data.error.message
              : 'Login failed. Please check your credentials.';
          set({ isLoading: false, error: message, isAuthenticated: false });
          throw err;
        }
      },

      logout: () => {
        localStorage.removeItem('client-portal-token');
        set({ client: null, accessToken: null, isAuthenticated: false, error: null });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'client-portal-storage',
      partialize: state => ({
        client: state.client,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => state => {
        if (state?.accessToken) {
          localStorage.setItem('client-portal-token', state.accessToken);
        }
      },
    }
  )
);
